import { IMAGE_URL } from "../api";
import { getThemeToken } from "../theme/runtimeTheme";

/** Shorter tail = smaller map footprint; bulb unchanged for a bigger logo vs body. */
const PIN_PATH_STR =
  "M26,2.6 C15.24,2.6 6.5,11.34 6.5,22.1 C6.5,29.15 26,43.6 26,43.6 S45.5,29.15 45.5,22.1 C45.5,11.34 36.76,2.6 26,2.6 Z";

const PIN_VIEWBOX = 52;
/** Tip Y in viewBox units — must match path */
const PIN_ANCHOR_Y = 43.6;

const CACHE_VER = "v14";

/** On-map pin size (idle + active share this so the anchor never jumps on hover). */
const PIN_SIZE_HI = 52;

const DEF_PIN_STROKE = "#f8fafc";
const DEF_PIN_STROKE_GOLD = "rgba(234, 88, 12, 0.72)";
const DEF_LOGO_PORT_CENTER = "#ffffff";
const DEF_LOGO_PORT_EDGE = "#f0ece4";
const DEF_LOGO_RING_IDLE = "#ea580c";
const DEF_LOGO_RING_ACTIVE = "#f0d78c";
const DEF_LOGO_RING_BRONZE = "#5c4a1f";
const DEF_LOGO_RING_DEEP = "#c2410c";

function pinT(key, fallback) {
  return getThemeToken(key) || fallback;
}

const dataUrlCache = new Map();

export function clearMapPinRasterCache() {
  dataUrlCache.clear();
}

/** API often returns a path; sometimes a full URL — avoid double base prefix */
export function resolveMediaUrl(raw) {
  if (!raw || typeof raw !== "string") return "";
  const s = raw.trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(IMAGE_URL || "").replace(/\/$/, "");
  const path = s.replace(/^\/+/, "");
  if (!base) return path;
  return `${base}/${path}`;
}

function loadImageForCanvas(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => {
      const img2 = new Image();
      img2.onload = () => resolve(img2);
      img2.onerror = () => reject(new Error("logo load failed"));
      img2.src = src;
    };
    img.src = src;
  });
}

/** `isHighlighted` = selected / active listing — body uses brand deep; idle pins stay black. */
function drawLuxuryPinBody(ctx, pinPath, isHighlighted) {
  ctx.save();
  if (isHighlighted) {
    ctx.fillStyle = pinT("primary_deep", "#c2410c");
  } else {
    ctx.fillStyle = pinT("map_pin_body_idle", "#000000");
  }
  ctx.fill(pinPath);
  ctx.restore();

  ctx.strokeStyle = pinT("pin_stroke_light", DEF_PIN_STROKE);
  ctx.lineWidth = isHighlighted ? 2.35 : 1.85;
  ctx.stroke(pinPath);
  ctx.strokeStyle = isHighlighted ? pinT("pin_stroke_gold", DEF_PIN_STROKE_GOLD) : pinT("footer_muted", "#6e6b66");
  ctx.lineWidth = isHighlighted ? 1.15 : 0.82;
  ctx.stroke(pinPath);
}

function drawPearlDot(ctx, isHighlighted) {
  const r = isHighlighted ? 5.15 : 5.45;
  const cx = 26;
  const cy = 16.35;
  const rg = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.35, 0, cx, cy, r);
  rg.addColorStop(0, "#ffffff");
  rg.addColorStop(0.55, "#f1f5f9");
  rg.addColorStop(1, "#cbd5e1");
  ctx.fillStyle = rg;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = isHighlighted
    ? pinT("pin_ring_active", DEF_LOGO_RING_ACTIVE)
    : pinT("pin_ring_idle", DEF_LOGO_RING_IDLE);
  ctx.lineWidth = isHighlighted ? 2.1 : 1.55;
  ctx.stroke();
  ctx.strokeStyle = pinT("pin_ring_bronze", DEF_LOGO_RING_BRONZE);
  ctx.lineWidth = 0.65;
  ctx.stroke();
}

function drawLogoMedallion(ctx, img, cx, cy, innerR, isHighlighted) {
  const imgPad = innerR * 0.035;
  const drawW = innerR * 2 - imgPad * 2;
  const drawLeft = cx - innerR + imgPad;
  const drawTop = cy - innerR + imgPad;
  const rBronze = innerR + 1.55;
  const rGold = innerR + 0.92;
  const rLip = innerR + 0.28;

  ctx.beginPath();
  ctx.arc(cx, cy, rBronze, 0, Math.PI * 2);
  ctx.strokeStyle = isHighlighted ? pinT("pin_ring_bronze", DEF_LOGO_RING_BRONZE) : pinT("footer_muted", "#6e6b66");
  ctx.lineWidth = 0.85;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, rGold, 0, Math.PI * 2);
  const ringGrad = ctx.createLinearGradient(cx - innerR, cy - innerR, cx + innerR, cy + innerR);
  if (isHighlighted) {
    ringGrad.addColorStop(0, pinT("primary_soft_bg", "#ffedd5"));
    ringGrad.addColorStop(0.45, pinT("pin_ring_active", DEF_LOGO_RING_ACTIVE));
    ringGrad.addColorStop(1, pinT("pin_ring_deep", DEF_LOGO_RING_DEEP));
  } else {
    ringGrad.addColorStop(0, pinT("footer_heading", "#f5f2eb"));
    ringGrad.addColorStop(0.45, pinT("footer_text", "#b9b6b0"));
    ringGrad.addColorStop(1, pinT("footer_bg_soft", "#121212"));
  }
  ctx.strokeStyle = ringGrad;
  ctx.lineWidth = isHighlighted ? 1.85 : 1.35;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, rLip, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.92)";
  ctx.lineWidth = 0.95;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  const portGrad = ctx.createRadialGradient(
    cx - innerR * 0.35,
    cy - innerR * 0.35,
    0,
    cx,
    cy,
    innerR
  );
  portGrad.addColorStop(0, pinT("pin_logo_port_center", DEF_LOGO_PORT_CENTER));
  portGrad.addColorStop(1, pinT("pin_logo_port_edge", DEF_LOGO_PORT_EDGE));
  ctx.fillStyle = portGrad;
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.clip();
  if (typeof ctx.imageSmoothingEnabled === "boolean") ctx.imageSmoothingEnabled = true;
  if ("imageSmoothingQuality" in ctx) ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, drawLeft, drawTop, drawW, drawW);
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.clip();
  const vignette = ctx.createRadialGradient(cx, cy, innerR * 0.35, cx, cy, innerR);
  vignette.addColorStop(0, "rgba(0,0,0,0)");
  vignette.addColorStop(1, "rgba(15,23,42,0.028)");
  ctx.fillStyle = vignette;
  ctx.fillRect(cx - innerR - 1, cy - innerR - 1, innerR * 2 + 2, innerR * 2 + 2);
  ctx.restore();
}

/**
 * Raster project pin — luxury slate body + jewel-framed logo (or pearl dot).
 */
export async function getMapPinDataUrl(logoUrlResolved, isHighlighted) {
  const key = `${CACHE_VER}|${logoUrlResolved || ""}|${isHighlighted ? "1" : "0"}`;
  if (dataUrlCache.has(key)) return dataUrlCache.get(key);

  /* One output size for idle + active so fitBounds/anchor never shift on hover (tip stays on lat/lng). */
  const displaySize = PIN_SIZE_HI;
  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio
      ? Math.min(3, Math.max(1.5, window.devicePixelRatio || 1))
      : 2.5;
  const vb = PIN_VIEWBOX;
  const scale = (displaySize / vb) * dpr;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(displaySize * dpr);
  canvas.height = Math.round(displaySize * dpr);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }

  if (typeof ctx.imageSmoothingEnabled === "boolean") ctx.imageSmoothingEnabled = true;
  if ("imageSmoothingQuality" in ctx) ctx.imageSmoothingQuality = "high";

  ctx.scale(scale, scale);
  const pinPath = new Path2D(PIN_PATH_STR);

  drawLuxuryPinBody(ctx, pinPath, isHighlighted);

  if (logoUrlResolved) {
    try {
      const img = await loadImageForCanvas(logoUrlResolved);
      const innerR = isHighlighted ? 13.1 : 11.05;
      drawLogoMedallion(ctx, img, 26, 16.35, innerR, isHighlighted);
    } catch {
      drawPearlDot(ctx, isHighlighted);
    }
  } else {
    drawPearlDot(ctx, isHighlighted);
  }

  let dataUrl;
  try {
    dataUrl = canvas.toDataURL("image/png");
  } catch {
    dataUrl = null;
  }
  if (dataUrl) {
    dataUrlCache.set(key, dataUrl);
  }
  return dataUrl;
}

/**
 * SVG fallback — active body: primary_deep; idle: black.
 * Optional `isHovered` only tightens strokes when idle (map hover feedback without gold body).
 */
export function getMapPinSvgFallback(isHighlighted, isHovered = false) {
  const size = PIN_SIZE_HI;
  const strokeW = isHighlighted ? 2.35 : isHovered ? 2.12 : 1.95;
  const goldW = isHighlighted ? 1.15 : isHovered ? 0.95 : 0.88;
  const dotR = isHighlighted ? 5.15 : 5.45;
  const ringCol = isHighlighted
    ? pinT("pin_ring_active", DEF_LOGO_RING_ACTIVE)
    : pinT("footer_heading", "#f5f2eb");
  const strokeLight = pinT("pin_stroke_light", DEF_PIN_STROKE);
  const strokeOuter = isHighlighted ? pinT("pin_stroke_gold", DEF_PIN_STROKE_GOLD) : pinT("footer_muted", "#6e6b66");
  const ringBronze = isHighlighted ? pinT("pin_ring_bronze", DEF_LOGO_RING_BRONZE) : pinT("footer_muted", "#6e6b66");
  const deep = pinT("primary_deep", "#c2410c");
  const black = pinT("map_pin_body_idle", "#000000");
  const uid = isHighlighted ? "luxH" : "luxN";
  const bodyGrad = isHighlighted
    ? `<linearGradient id="${uid}g" x1="4" y1="2" x2="48" y2="52" gradientUnits="userSpaceOnUse"><stop stop-color="${deep}"/><stop offset="1" stop-color="${deep}"/></linearGradient>`
    : `<linearGradient id="${uid}g" x1="4" y1="2" x2="48" y2="52" gradientUnits="userSpaceOnUse"><stop stop-color="${black}"/><stop offset="1" stop-color="${black}"/></linearGradient>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 52 52"><defs>${bodyGrad}<radialGradient id="${uid}p" cx="38%" cy="35%" r="65%"><stop stop-color="#ffffff"/><stop offset="0.55" stop-color="#f1f5f9"/><stop offset="1" stop-color="#cbd5e1"/></radialGradient></defs><path d="${PIN_PATH_STR}" fill="url(#${uid}g)" stroke="${strokeLight}" stroke-width="${strokeW}" stroke-linejoin="round"/><path d="${PIN_PATH_STR}" fill="none" stroke="${strokeOuter}" stroke-width="${goldW}" stroke-linejoin="round"/><circle cx="26" cy="16.35" r="${dotR}" fill="url(#${uid}p)" stroke="${ringCol}" stroke-width="${isHighlighted ? 2.1 : 1.35}"/><circle cx="26" cy="16.35" r="${dotR}" fill="none" stroke="${ringBronze}" stroke-width="0.65"/></svg>`;
  const ay = Math.round((size * PIN_ANCHOR_Y) / PIN_VIEWBOX);
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize:
      typeof window !== "undefined" && window.google?.maps
        ? new window.google.maps.Size(size, size)
        : undefined,
    anchor:
      typeof window !== "undefined" && window.google?.maps
        ? new window.google.maps.Point(Math.round(size / 2), ay)
        : undefined,
  };
}

export function googleIconFromDataUrl(dataUrl, _isHighlighted) {
  if (typeof window === "undefined" || !dataUrl || !window.google?.maps) return undefined;
  const size = PIN_SIZE_HI;
  const ay = Math.round((size * PIN_ANCHOR_Y) / PIN_VIEWBOX);
  return {
    url: dataUrl,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(Math.round(size / 2), ay),
  };
}

/**
 * Search reference — precision target, not a listing teardrop.
 */
export function getUserSearchCenterPinIcon() {
  const size = 40;
  const gold = pinT("primary", DEF_LOGO_RING_IDLE);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 40 40"><circle cx="20" cy="20" r="14" fill="#ffffff" stroke="#64748b" stroke-width="2"/><circle cx="20" cy="20" r="5" fill="${gold}"/></svg>`;
  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  if (typeof window === "undefined" || !window.google?.maps) {
    return { url };
  }
  const ax = Math.round(size / 2);
  return {
    url,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(ax, ax),
  };
}
